/**
 * CATALOGO DEGLI OPERANDI
 */
export const OPERAND_DB = {
  CODICE: "Codice dell'operazione (es. 10, 11, 12)",
  REP: "Indice del reparto (1-99)",
  PRE: "Importo in centesimi se usato con comando VEND; Prezzo di default del reparto (in genere 0, due decimali) se usato con il comando PREP",
  DES: "Descrizione testuale riga (massimo 16 caratteri se usato con comando PREP)",
  QTY: "Quantità",
  T: "Tipo di pagamento",
  IMP: "Importo pagato",
  NF: "Numero file da leggere",
  FILE: "File su cui scrivere",
  APPEND: "Accoda al file precedente",
  PATH: "Percorso del file",
  TIPO: "Tipologia di azzeramento",
  NR: "Codice IVA su RT se usato con comando PIVA; numero reparto, max 40 se usato con comando PREP",
  ALIQ: "Aliquota IVA (2 decimali)",
  LIS: "Limite massimo di prezzo per una singola vendita nel reparto (default: 10000.00 - due decimali)",
  IVA: "Codice IVA associato",
  SERVIZIO: "Tipo di bene: NO per reparto trattato come bene, SI per reparto trattato come servizio",
  SCONTI: "Abilitazione sconti (default NO sconti non ammessi)",
  VAL: "Valore di sconto o maggiorazione su linea o subtotale",
  SUBT: "Subtotale del documento",
  RIGA: "Testo da stampare",
  TERM: "Codice numerico per identificare un input specifico",
  ON: "Fa partire la sezione di inserimento degli allegati",
  FINE: "Conclude la sezione di inserimento degli allegati",
  BARCODE: "Barcode da stampare",
  ZNUMBER: "Numero di azzeramento del documento (per annullo/reso). Corrisponde alle prime 4 cifre del codice riportato in coda al documento d'origine.",
  NUMSCO: "Numero progressivo del documento (per annullo/reso). Corrisponde alle ultime 4 cifre del codice riportato in coda al documento d'origine.",
  DATASCO: "Data di emissione del documento (formato GGMMAA)",
  MATR: "Matricola del RT che ha emesso il documento",
  AUTOMATICO: "Consente di annullare automaticamente il documento se presente nella memoria permanente di dettaglio (DGFE). In questo caso non sarà necessario ripetere la vendita dei prodotti da annullare.",
  CMD: "Codice del comando interno del RT",
  DATA: "Dati da inviare al comando interno del RT",
  ALFA: "Codice lotteria",
  NUM: "Tipologia di report da stampare",
  APRI: "Apre il documento gestionale",
  CHIUDI: "Chiude il documento gestionale",
  OMAGGIO: "Specifica se l'articolo è un omaggio (prezzo 0)",
  STORNO: "Identifica l'annullamento di un articolo precedentemente inserito nel documento",
  STOACC: "Indica lo storno di un acconto (anticipo) precedentemente versato dal cliente",
  BUOMON: "Identifica lo storno o l'utilizzo di un buono monouso (voucher o coupon)",
  OPE1: "Riga 1 del display operatore",
  OPE2: "Riga 2 del display operatore",
  CLI1: "Riga 1 del display cliente",
  CLI2: "Riga 2 del display cliente"
} as const;

export type OperandKey = keyof typeof OPERAND_DB;

/**
 * CATALOGO DELLE ISTRUZIONI
 * Elenco dei comandi associati ai loro operandi obbligatori e opzionali
 */
export const INSTRUCTION_CATALOG = {
  CLEAR: {
    desc: "Rimuove eventuali errori dal registratore",
    mandatory: [] as const,
    optional: [] as const,
  },
  WECLOCK: {
    desc: "Acquisisce un LOCK",
    mandatory: [] as const,
    optional: [] as const,
  },
  WECUNLOCK: {
    desc: "Libera un LOCK",
    mandatory: [] as const,
    optional: [] as const,
  },
  RESPRN: {
    desc: "Annulla documenti aperti eventualmente in sospeso",
    mandatory: [] as const,
    optional: [] as const,
  },
  CHIAVE: {
    desc: "Definisce la modalità operativa della stampante",
    mandatory: ["MODE"] as const,
    optional: [] as const,
  },
  INFO: {
    desc: "Verifica dati RT",
    mandatory: ["CODICE"] as const,
    optional: [] as const,
  },
  VEND: {
    desc: "Esegue la vendita di un articolo",
    mandatory: ["REP", "PRE", "DES"] as const,
    optional: ["QTY", "OMAGGIO", "STORNO", "STOACC", "BUOMON"] as const,
  },
  CHIUS: {
    desc: "Chiude il documento commerciale di vendita, reso o annullo",
    mandatory: ["T"] as const,
    optional: ["IMP"] as const,
  },
  WECFINE: {
    desc: "Termina l’elaborazione della sequenza comandi",
    mandatory: [] as const,
    optional: [] as const,
  },
  LEGGI: {
    desc: "Legge le programmazioni del RT",
    mandatory: ["NF", "FILE"] as const,
    optional: ["APPEND"] as const,
  },
  WECDOWNLOAD: {
    desc: "Scarica il contenuto del file specificato",
    mandatory: ["PATH"] as const,
    optional: [] as const,
  },
  AZZGIO: {
    desc: "Esegue l'azzeramento giornaliero",
    mandatory: ["TIPO"] as const,
    optional: [] as const,
  },
  PROG: {
    desc: "Attiva la sessione di programmazione",
    mandatory: [] as const,
    optional: [] as const,
  },
  FINEPROG: {
    desc: "Chiude la sessione di programmazione",
    mandatory: [] as const,
    optional: [] as const,
  },
  PIVA: {
    desc: "Programma l'aliquota IVA",
    mandatory: ["NR", "ALIQ"] as const,
    optional: [] as const,
  },
  PREP: {
    desc: "Programma il reparto",
    mandatory: ["NR", "DES", "IVA"] as const,
    optional: ["LIS", "PRE", "SERVIZIO", "SCONTI"] as const,
  },
  PINT: {
    desc: "Programma l'intestazione",
    mandatory: ["ROWS"] as const,
    optional: [] as const,
  },
  SCONTO: {
    desc: "Applica uno sconto a valore su linea o su subtotale",
    mandatory: ["VAL"] as const,
    optional: ["DES", "REP", "SUBT"] as const,
  },
  MAGGIORA: {
    desc: "Applica una maggioranzione a valore su linea o su subtotale",
    mandatory: ["VAL"] as const,
    optional: ["DES", "REP", "SUBT"] as const,
  },
  PERCA: {
    desc: "Applica uno sconto % su linea o su subtotale",
    mandatory: ["ALIQ"] as const,
    optional: ["DES", "REP", "SUBT"] as const,
  },
  PERCB: {
    desc: "Applica una maggioranzione % su linea o su subtotale",
    mandatory: ["VAL"] as const,
    optional: ["DES", "REP", "SUBT"] as const,
  },
  PRMSG: {
    desc: "Stampa riga libera su scontrino",
    mandatory: ["RIGA"] as const,
    optional: [] as const,
  },
  INP: {
    desc: "Input generico",
    mandatory: ["TERM"] as const,
    optional: ["ALFA"] as const,
  },
  ALLEG: {
    desc: "Consente l'inserimento di righe di allegato al documento commerciale",
    mandatory: [] as const,
    optional: ["ON", "RIGA", "BARCODE", "FINE"] as const,
  },
  DOCANNULLO: {
    desc: "Inizia un documento commerciale di annullo",
    mandatory: ["MATR", "DATASCO", "NUMSCO", "ZNUMBER"] as const,
    optional: ["AUTOMATICO"] as const,
  },
  NOTACRED: {
    desc: "Inizia un documento commerciale di reso",
    mandatory: ["MATR", "DATASCO", "NUMSCO", "ZNUMBER"] as const,
    optional: [] as const,
  },
  DIRECTIO: {
    desc: "Consente l'invio di comandi personalizzati al RT",
    mandatory: ["CMD", "DATA"] as const,
    optional: [] as const,
  },
  REPORT: {
    desc: "Effettua la stampa di report su RT",
    mandatory: ["NUM"] as const,
    optional: [""] as const,
  },
  NOFIS: {
    desc: "Inizia un documento commerciale gestionale",
    mandatory: [""] as const,
    optional: ["APRI", "RIGA", "BARCODE", "CHIUDI"] as const,
  },
  VIS: {
    desc: "Consente l'invio di descrizioni sul display",
    mandatory: [""] as const,
    optional: ["OPE1", "OPE2", "CLI1", "CLI2"] as const,
  }
} as const;

export type InstructionName = keyof typeof INSTRUCTION_CATALOG;


export type CommandLine<T extends InstructionName> = {
  type: "COMMAND";
  instruction: T;
  operands?: {
    [K in (typeof INSTRUCTION_CATALOG)[T]["mandatory"][number] & OperandKey]: string | number;
  } & {
    [K in (typeof INSTRUCTION_CATALOG)[T]["optional"][number] & OperandKey]?: string | number;
  };
  comment?: string;
};

export type RealCommand = { [K in InstructionName]: CommandLine<K> }[InstructionName];

export type DitronPrinterCommand =
  | RealCommand
  | { type: "COMMENT_ONLY"; text: string };


export const POSITIONAL_INSTRUCTIONS: InstructionName[] = ["WECDOWNLOAD", "CHIAVE", "PROG", "PINT"];

export const APEX_STRING_OPERAND: OperandKey[] = [
  "DES", "FILE", "RIGA", "DATA", "MATR", "ALFA",
  "OPE1", "OPE2", "CLI1", "CLI2"
];

export interface DitronPrinterResponse<T = any> {
  success: boolean;
  timestamp: string;
  data: T;
  details: DitronCommandResult[];
}

export interface PrinterStatusData {
  online: boolean;
  statusMessage: string;
}

export interface TextOptions {
  wrap: boolean;
  maxLength?: number;
}

export const maxLengthDesc = 25;
export const maxLengthMsg = 40;

export const CHIAVE_MODES = {
  REG: "REG",
  X: "X",
  Z: "Z",
  P: "P"
} as const;

export interface PrinterDepartment {
  nr: string;
  des: string;
  iva: string;
  [key: string]: string;
}

export interface PrinterIva {
  nr: string;
  aliq: string;
}

export interface PrinterHeader {
  [key: string]: string;
}

export interface DitronPrinterConfig {
  departments: PrinterDepartment[];
  vats: PrinterIva[];
  headers: PrinterHeader;
}

export type DitronPayment = 
  "CONTANTI" | "CREDITO_GENERICO" | "ASSEGNI" | 
  "CARTA_DI_CREDITO" | "NON_RISCOSSO_SERVIZI" | "NON_RISCOSSO_SSN" |
  "NON_RISCOSSO_FATTURE" | "TICKET" | "BUONI_MULTIUSO" | "SCONTO_A_PAGARE";

export const DITRON_PAYMENT_MAP: Record<DitronPayment, number> = {
  'CONTANTI': 1,
  'CREDITO_GENERICO': 2,
  'ASSEGNI': 3,
  'CARTA_DI_CREDITO': 5,
  'NON_RISCOSSO_SERVIZI': 11,
  "NON_RISCOSSO_SSN": 12,
  "NON_RISCOSSO_FATTURE": 13,
  "TICKET": 14,
  "BUONI_MULTIUSO": 14,
  "SCONTO_A_PAGARE": 30
};

export const barcodeTypeCodesDitronPrinter: Record<string, string> = {
    'EAN13': '1',
    'EAN8': '1',
    'CODE39': '108',
    'EAN128': '120',
    'QRCODE': '158'
};

export const defaultBarcodeTypeDitron = '108';

export const errorDitronPrinter: Record<string, string> = {
    '2/106': 'E_ILLEGAL',
    '2/109': 'E_NOEXIST',
    '2/111': 'E_FAILURE',
    '2/112': 'E_TIMEOUT',
    '2/113': 'E_BUSY',
    '4/201': 'EFPTR_COVER_OPEN',
    '4/202': 'EFPTR_JRN_EMPTY',
    '4/203': 'EFPTR_REC_EMPTY',
    '4/206': 'EFPTR_MISSING_DEVICES',
    '4/207': 'EFPTR_WRONG_STATE',
    '4/208': 'EFPTR_TECHNICAL_ASSISTANCE',
    '4/212': 'EFPTR_FISCAL_TOTALS_ERROR',
    '4/213': 'EFPTR_BAD_ITEM_QUANTITY',
    '4/214': 'EFPTR_BAD_ITEM_AMOUNT',
    '4/215': 'EFPTR_BAD_ITEM_DESCRIPTION',
    '4/216': 'EFPTR_RECEIPT_TOTAL_OVERFLOW',
    '4/217': 'EFPTR_BAD_VAT',
    '4/218': 'EFPTR_BAD_PRICE',
    '4/219': 'EFPTR_BAD_DATE',
    '4/220': 'EFPTR_NEGATIVE_TOTAL',
    '4/221': 'EFPTR_WORD_NOT_ALLOWED',
    '4/222': 'EFPTR_BAD_LENGTH',
    '4/224': 'EFPTR_DAY_END_REQUIRED'
}




// ----- RISPOSTA

export interface DitronCommandResult {
  instruction: string;
  rawResponse: string;
  status: 'SUCCESS' | 'ERROR' | 'INFO';
  message: string;
  errorCode?: string;
  data?: {
    serial?: string;
    date?: string;
    zNumber?: number;
    docNumber?: number;
    [key: string]: any;
  }
}

export const RESPONSE_MAP:  Partial<Record<InstructionName, Record<string, string>>> = {
  INFO: {
    "1": "Immatricolato",
    "2": "Censito",
    "3": "Attivato",
    "4": "In servizio",
    "5": "Dismessa",
    "6": "Fuori servizio"
  }
}